// 入力履歴
import React, { useState, useEffect, useCallback } from 'react';
import { Grid } from 'gridjs-react';
import { render } from 'react-dom';
import axios from '../../axios';
import DatePickerJp from '../../organisms/DatePickerJp';
import Moment from 'moment';
import { handleBrowserBack } from '../../BrowserBack';
import HistoryResultList from '../../molecules/HistoryResultList';
import LoadingModal from '../../molecules/LoadingModal';
import { jumpUrl } from './../../Common';
import TopArea from '../../molecules/TopArea';
import { Button } from 'react-bootstrap';
import { header_info_init, reception_history_search_params_init } from '../../Const';
import { Form, InputGroup } from 'react-bootstrap';
import { KeyValue } from '../../types/KeyValue';

// 検索パラメータのインターフェースを定義
type SearchParams = {
  [key: string]: string | KeyValue[];
  from_update_at: string;
  to_update_at: string;
  update_user_type: Array<KeyValue>;
  update_name: string;
  status: string;
  item_name: string;
  item_old_value: string;
  item_new_value: string;
};
type ReceptionHistoryHeader = {
  application_product: string;
  is_history: boolean;
  is_staff: boolean;
  reception_cd: string;
  status_code: number;
  status_name: string;
  user_name: string;
};
type RowSelection = {
  [key: string]: boolean;
};
// typescriptエラー防止 : Bladeから取得するトークンの型が string | null
interface Props {
  r_cd: string | null;
}
const ReceptionHistory: React.FC<Props> = ({ r_cd }) => {
  const [receptions, setReceptions] = useState([]);
  const [update_status_list, setUpdateStatusList] = useState<KeyValue[]>();
  const [uesr_update_type_list, setUserUpdateTypeList] = useState<KeyValue[]>();
  const [error_message, setErrorMessage] = useState();
  // Ajax通信中フラグ
  const [is_connecting, setIsConnecting] = useState(false);

  // 検索パラメータの初期化
  const [search_params, setSearchParams] = useState<SearchParams>(
    reception_history_search_params_init
  );
  const [is_loading, setIsLoading] = useState(false);
  //案件履歴ヘッダー情報
  const [reception_history_header, setReceptionHistoryHeader] = useState<ReceptionHistoryHeader>(
    header_info_init
  );

  // 行選択の状態管理
  const [row_selection, setRowSelection] = useState<RowSelection>({});

  // 検索ボタン押下
  const handleClickSearch = useCallback(() => {
    // クリックイベント
    const searchReceptionHistory = async () => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        if (is_connecting) {
          return;
        }
        setIsConnecting(true);

        const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/staff/api/history/search/${r_cd}`, search_params);
        const newReceptions = response.data.reception_history_list; // state更新のため一旦変数に移し替える

        setReceptions(newReceptions);
        // エラーメッセージを初期化
        setErrorMessage(undefined);
        setIsConnecting(false);
      } catch (error) {
        console.error(error.response.data.message);
        jumpUrl(error.response.data.url);
        setErrorMessage(error.response.data.message);
        setIsConnecting(false);
      } finally {
        //ローディング画面非表示
        setIsLoading(false);
      }
    };
    searchReceptionHistory();
  }, [search_params, is_connecting]);
  // 検索パラメータを設定
  const handleChangeSearchParams = (target: string, param: string | Date | RowSelection) => {
    const key: string = target as string; // targetの型を明示的に指定
    let setParam: string | KeyValue[] = typeof param === 'string' ? '' : [];
    const params = Object.assign({}, search_params) as SearchParams;

    if (
      Object.prototype.toString
        .call(param)
        .slice(8, -1)
        .toLowerCase() === 'date'
    ) {
      // Date型
      setParam = Moment(param).format('YYYY-MM-DD');
    } else if (typeof param === 'string') {
      // string型
      setParam = param;
    } else if (typeof param === 'object') {
      // object型
      // 更新者種別リストと照合してリクエストに必要なデータを生成する
      const uesr_update_type_details: KeyValue[] = [];
      Object.keys(row_selection).map(key => {
        if (row_selection[key]) {
          // 更新者種別リストと照合する
          uesr_update_type_list?.map(uesr_update_type => {
            const uesr_update_type_key = uesr_update_type['key'].toString();
            if (uesr_update_type_key === key) {
              uesr_update_type_details.push(uesr_update_type);
            }
          });
        }
      });
      setParam = uesr_update_type_details;
    }

    params[key] = setParam;
    setSearchParams(params);
  };

  /**
   * チェックボックスのcheckedを操作する
   * @param index 案件ID
   */
  const rowSelection = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const index = event?.target.value;
      const new_row_selection = row_selection;
      const keys = Object.keys(row_selection);
      if (keys.length === 0) {
        new_row_selection[index] = true;
      } else {
        if (keys.includes(index)) {
          new_row_selection[index] = !new_row_selection[index];
        } else {
          new_row_selection[index] = true;
        }
      }
      handleChangeSearchParams('update_user_type', new_row_selection);
    },
    [row_selection, uesr_update_type_list, search_params]
  );

  // 画面初期表示
  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    const getReception = async () => {
      try {
        const response = await axios.get(`${location.origin}${process.env.MIX_MOCK_URL}/staff/api/history/init/${r_cd}`);
        setReceptions(response.data.reception_history_list);
        setUpdateStatusList(response.data.update_status_list);
        setUserUpdateTypeList(response.data.user_update_type_list);
        setReceptionHistoryHeader(response.data.reception_history_header);
      } catch (error) {
        console.error(error);
        setErrorMessage(error.response.data.message);
      }
    };
    getReception();
  }, []);

  const handleClickClear = () => {
    const new_search_param = reception_history_search_params_init;
    setSearchParams(new_search_param);
    setRowSelection({});
  };

  return (
    <div className="container-fluid">
      <div className="reception-history-search-form">
        <div className="row my-2">
          <div className="col-md-4 col-sm-12">
            <span className="search-criteria-head">更新日</span>
            <DatePickerJp
              id="search-update-from"
              className="search-update-at"
              handleChangeDate={(date: Date) => handleChangeSearchParams('from_update_at', date)}
              selected={
                search_params.from_update_at.length > 0
                  ? new Date(search_params.from_update_at)
                  : null
              }
              customInput={<input type="text" className="search-form-input" />}
            />
            ～
            <DatePickerJp
              id="search-update-to"
              className="search-update-at"
              handleChangeDate={(date: Date) => handleChangeSearchParams('to_update_at', date)}
              selected={
                search_params.to_update_at.length > 0 ? new Date(search_params.to_update_at) : null
              }
              customInput={<input type="text" className="search-form-input" />}
            />
          </div>
          <div className="col-md-3 col-sm-12">
            <span className="search-criteria-head">更新項目</span>
            <input
              type="text"
              className="search-form-item-name"
              name="更新項目"
              value={search_params.item_name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeSearchParams('item_name', event?.target.value)
              }
            />
          </div>
        </div>
        <div className="row my-2">
          <div className="col-md-4 col-sm-12">
            <span className="search-criteria-head">更新者種別</span>
            <InputGroup>
              {uesr_update_type_list?.map((src, index) => {
                return (
                  <label className="check-box-label" key={src.key}>
                    <Form.Check
                      disabled={false}
                      id={'test'}
                      type="checkbox"
                      inline
                      value={src.key}
                      name={'name'}
                      onChange={rowSelection}
                      className="check-box"
                      checked={
                        row_selection[src.key] !== undefined && row_selection[src.key] !== null
                          ? row_selection[src.key]
                          : false
                      }
                    />
                    {src.value}
                  </label>
                );
              })}
            </InputGroup>
          </div>
          <div className="col-md-3 col-sm-12">
            <span className="search-criteria-head">更新者</span>
            <input
              type="text"
              className="search-form-update-name"
              name="更新者"
              value={search_params.update_name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeSearchParams('update_name', event?.target.value)
              }
            />
          </div>
          <div className="col-md-3 col-sm-12">
            <span className="search-criteria-head">更新時状態</span>
            <select
              value={search_params.status}
              className="search-form-status"
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                handleChangeSearchParams('status', event?.target.value)
              }
            >
              <option></option>
              {update_status_list?.map((option, index) => {
                return (
                  <option key={index} value={option.key}>
                    {option.key}：{option.value}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="col-md-auto text-right">
            <Button variant="main-color" className="btn-clear" onClick={handleClickClear}>
              クリア
            </Button>
            <button
              id="search-btn"
              className="btn btn-main-color"
              onClick={() => handleClickSearch()}
            >
              検　索
            </button>
          </div>
        </div>
      </div>
      <HistoryResultList data={receptions} />
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
    </div>
  );
};

if (document.getElementById('reception-history')) {
  // Bladeから案件IDを取得する
  let r_cd: string | null = document.getElementById('reception-history')!.getAttribute('r_cd');
  render(<ReceptionHistory r_cd={r_cd} />, document.getElementById('reception-history'));
}
